import { XCircleIcon } from '@heroicons/react/24/solid'
import { useBulkPermanentDelete, useBulkRestore } from '@qse/api'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { useIsOverQuota } from '@qse/stores'
import React from 'react'
import { Drawer } from 'vaul'

import {
  CopyIconDrop,
  DeleteIcon,
  DownloadIconDrop,
  MoveIcon,
  PreviewIcon,
  RenameIcon,
  StarIconMenu,
} from '../icons'

export type ContextMenuAction =
  | 'download'
  | 'preview'
  | 'rename'
  | 'duplicate'
  | 'favourite'
  | 'trash'
  | 'move'
  | 'restore'
  | 'deletePermanently'
  | 'abortUpload'

interface MobileActionDrawerProps {
  isOpen: boolean
  onClose: () => void
  onAction: (action: ContextMenuAction, path?: string) => void
  path?: string
  fileId?: string
  selectedFileIds?: (string | number)[]
  showRestore?: boolean
  trashMode?: boolean
  fileStatus?: string
  isFolder?: boolean
  fileData?: any
  onDeleteGhostFiles?: (fileIds: string[]) => Promise<void>
  systemFolderType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
}

export const MobileActionDrawer: React.FC<MobileActionDrawerProps> = ({
  isOpen,
  onClose,
  onAction,
  path,
  fileId,
  selectedFileIds,
  showRestore = false,
  trashMode = false,
  fileStatus,
  isFolder = false,
  fileData,
  onDeleteGhostFiles,
  systemFolderType,
}) => {
  const isOverQuota = useIsOverQuota()
  const { mutateAsync: deleteForever } = useBulkPermanentDelete()
  const { mutateAsync: restoreFiles } = useBulkRestore()

  // Check if preview should be shown
  // Preview is now available for all file types (supported and unsupported)
  const isPreviewable =
    !isFolder &&
    fileData &&
    fileId != null &&
    (!selectedFileIds?.includes(fileId) ||
      (selectedFileIds?.length === 1 && selectedFileIds.includes(fileId)))

  // Check if this is a ghost file
  const isGhostFile =
    fileStatus === 'UPLOADING' || fileStatus === 'ENCRYPTING' || fileStatus === 'FAILED'

  const handleAction = (action: ContextMenuAction) => {
    onAction(action, path || '')
    onClose()
  }

  const handleDeletePermanently = () => {
    const fileIds =
      selectedFileIds && selectedFileIds.length > 0
        ? selectedFileIds.map(id => id.toString())
        : fileId
          ? [fileId.toString()]
          : []

    if (fileIds.length > 0) {
      const itemCount = fileIds.length
      const title =
        itemCount === 1
          ? `Delete "${fileData?.name || 'this file'}" Permanently?`
          : `Delete ${itemCount} files Permanently?`
      const message =
        itemCount === 1
          ? 'File processing may be in progress or has failed. Large files can take considerable time to process. Canceling now will permanently remove this file.'
          : 'File processing may be in progress or has failed. Large files can take considerable time to process. Canceling now will permanently remove these files.'

      useActiveModelStore.setState({
        width: 'max-w-[490px]',
        modelActive: ModelControlEnum.CONFIRMATION,
        data: {
          title,
          message,
          icon: '/assets/svg/warning.svg',
          actions: [
            {
              label: 'Cancel',
              color: 'default',
              variant: 'flat',
            },
            {
              label: 'Delete Permanently',
              color: 'danger',
              variant: 'solid',
              onPress: async () => {
                if (onDeleteGhostFiles && isGhostFile) {
                  await onDeleteGhostFiles(fileIds)
                } else {
                  onAction('deletePermanently', path || '')
                }
                onClose()
              },
            },
          ],
        },
        toggleModel: true,
      })
    }
  }

  const handleRestore = () => {
    const fileIds =
      selectedFileIds && selectedFileIds.length > 0
        ? selectedFileIds.map(id => id.toString())
        : fileId
          ? [fileId.toString()]
          : []

    if (fileIds.length > 0) {
      const itemCount = fileIds.length
      let title: string

      if (itemCount === 1) {
        // Get the filename for single file restoration
        const fileName = fileData?.name || 'this item'

        title = `Restore "${fileName}"?`
      } else {
        title = `Restore ${itemCount} items?`
      }
      const message =
        itemCount === 1
          ? 'This file or folder will be restored from trash.'
          : 'These files and folders will be restored from trash.'

      useActiveModelStore.setState({
        width: 'max-w-[500px]',
        modelActive: ModelControlEnum.CONFIRMATION,
        data: {
          title,
          message,
          icon: '/assets/svg/warning.svg',
          actions: [
            {
              label: 'Cancel',
              variant: 'flat',
            },
            {
              label: 'Restore',
              color: 'primary',
              variant: 'solid',
              onPress: async () => {
                await restoreFiles({ fileIds })
                onClose()
              },
            },
          ],
        },
        toggleModel: true,
      })
    }
  }

  const handleDeleteForever = () => {
    const fileIds =
      selectedFileIds && selectedFileIds.length > 0
        ? selectedFileIds.map(id => id.toString())
        : fileId
          ? [fileId.toString()]
          : []

    if (fileIds.length > 0) {
      const itemCount = fileIds.length
      let title: string

      if (itemCount === 1) {
        // Get the filename for single file deletion
        const fileName = fileData?.name || 'this item'

        title = `Delete "${fileName}" Forever?`
      } else {
        title = `Delete ${itemCount} items Forever?`
      }
      const message =
        itemCount === 1
          ? 'This file or folder will be permanently deleted and cannot be restored.'
          : 'These files and folders will be permanently deleted and cannot be restored.'

      useActiveModelStore.setState({
        width: 'max-w-[490px]',
        modelActive: ModelControlEnum.CONFIRMATION,
        data: {
          title,
          message,
          icon: '/assets/svg/warning.svg',
          actions: [
            {
              label: 'Cancel',
              color: 'default',
            },
            {
              label: 'Delete Forever',
              color: 'danger',
              variant: 'solid',
              onPress: async () => {
                await deleteForever({ fileIds })
                onClose()
              },
            },
          ],
        },
        toggleModel: true,
      })
    }
  }

  // Menu items - different logic for ghost files vs regular files vs system folders
  const menuItems = isGhostFile
    ? [
        // For ghost files, show ONLY the delete permanently action
        {
          label: 'Delete permanently',
          icon: XCircleIcon,
          action: 'deletePermanently' as ContextMenuAction,
          onClick: handleDeletePermanently,
        },
      ]
    : systemFolderType === 'CHAT_CONVERSATION' && !trashMode
      ? [
          // For CHAT_CONVERSATION folders, show ONLY the star/favorite action (not in trash mode)
          {
            label: fileData?.isFavorite === true ? 'Remove from favorites' : 'Favourites',
            icon: () => (
              <StarIconMenu
                className={`w-5 h-5 flex-shrink-0 ${
                  fileData?.isFavorite === true
                    ? 'text-yellow-400 fill-current'
                    : 'text-gray-900 dark:text-white'
                }`}
              />
            ),
            action: 'favourite' as ContextMenuAction,
            onClick: () => handleAction('favourite'),
          },
        ]
      : trashMode
        ? [
            // Trash mode items
            {
              label: 'Restore',
              icon: () => (
                <svg
                  className="w-5 h-5 text-gray-900 dark:text-white flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              ),
              action: 'restore' as ContextMenuAction,
              onClick: handleRestore,
            },
            {
              label: 'Delete Forever',
              icon: DeleteIcon,
              action: 'trash' as ContextMenuAction,
              onClick: handleDeleteForever,
            },
          ]
        : [
            // Regular menu items for non-ghost files
            ...(isPreviewable
              ? [
                  {
                    label: 'Preview',
                    icon: PreviewIcon,
                    action: 'preview' as ContextMenuAction,
                    onClick: () => handleAction('preview'),
                  },
                ]
              : []),
            ...(!isFolder && selectedFileIds && selectedFileIds.length <= 20
              ? [
                  {
                    label: 'Download',
                    icon: DownloadIconDrop,
                    action: 'download' as ContextMenuAction,
                    onClick: () => handleAction('download'),
                    disabled: fileStatus && fileStatus !== 'AVAILABLE',
                  },
                ]
              : []),
            {
              label: 'Rename',
              icon: RenameIcon,
              action: 'rename' as ContextMenuAction,
              onClick: () => handleAction('rename'),
            },
            ...(!isFolder
              ? [
                  {
                    label: 'Make a copy',
                    icon: CopyIconDrop,
                    action: 'duplicate' as ContextMenuAction,
                    disabled: isOverQuota,
                    onClick: () => {
                      if (isOverQuota) return
                      handleAction('duplicate')
                    },
                  },
                ]
              : []),
            {
              label: 'Move to',
              icon: MoveIcon,
              action: 'move' as ContextMenuAction,
              onClick: () => handleAction('move'),
            },
            {
              label: fileData?.isFavorite === true ? 'Remove from favorites' : 'Favourites',
              icon: () => (
                <StarIconMenu
                  className={`w-5 h-5 flex-shrink-0 ${
                    fileData?.isFavorite === true
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-900 dark:text-white'
                  }`}
                />
              ),
              action: 'favourite' as ContextMenuAction,
              onClick: () => handleAction('favourite'),
            },
            ...(showRestore
              ? [
                  {
                    label: 'Restore',
                    icon: () => (
                      <svg
                        className="w-5 h-5 text-gray-900 dark:text-white flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                        />
                      </svg>
                    ),
                    action: 'restore' as ContextMenuAction,
                    onClick: () => handleAction('restore'),
                  },
                ]
              : []),
            {
              label: 'Move to Trash',
              icon: DeleteIcon,
              action: 'trash' as ContextMenuAction,
              onClick: () => handleAction('trash'),
            },
          ]

  // Don't show mobile drawer for CHAT_ROOT folders (except in trash mode)
  if (systemFolderType === 'CHAT_ROOT' && !trashMode) {
    return null
  }

  return (
    <Drawer.Root open={isOpen} onOpenChange={onClose}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/40 z-[9998]" />
        <Drawer.Content className="bg-white dark:bg-[#343537] flex flex-col rounded-t-[10px] mt-24 h-fit z-[9999] fixed bottom-0 left-0 right-0 outline-none">
          <div className="p-4 bg-white dark:bg-[#343537] rounded-t-[10px] flex-1">
            <div
              aria-hidden
              className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-gray-300 dark:bg-gray-600 mb-6"
            />

            {/* File info header */}
            <div className="max-w-md mx-auto mb-6">
              <div className="flex items-center gap-3 mb-2">
                {fileData?.isDir ? (
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-blue-600 dark:text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-gray-600 dark:text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        clipRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                        fillRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 dark:text-white text-base truncate">
                    {fileData?.name || 'File'}
                  </h3>
                  {selectedFileIds && selectedFileIds.length > 1 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedFileIds.length} items selected
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="max-w-md mx-auto space-y-2">
              {menuItems.map(({ label, icon: Icon, onClick, action }) => {
                const isDisabled = isOverQuota && action === 'duplicate'

                return (
                  <button
                    key={label}
                    aria-disabled={isDisabled}
                    className={`w-full flex items-center gap-4 px-4 py-3 text-left rounded-lg transition-colors ${
                      isDisabled ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                    onClick={() => {
                      if (isDisabled) return
                      onClick()
                    }}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">{label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
